import React, { useMemo } from 'react';
import { TypedMemo } from '../../../common/types';
import { useArtistPlaylistScoreChart } from '../../hooks/useArtistPlaylistScoreChart';
import { useTrackPlaylistScoreChart } from '../../hooks/useTrackPlaylistScoreChart';
import { usePlaylistFollowersTimeSeries } from '../../hooks/usePlaylistFollowersTimeSeries';
import { MiniBarChart } from './MiniBarChart';
import styles from './MiniScoreChart.module.scss';

export type MiniScoreChartEntityType = 'artist' | 'track' | 'playlist';

interface MiniScoreChartProps {
  entityType: MiniScoreChartEntityType;
  entityId: string;
  date?: Date;
}

// We FETCH the full window (same params the detail pages use, so the row-fetch
// warms the cache for instant detail navigation) but only SHOW the last N days,
// so the bars stay readable (not thin hairlines) at ~72px wide.
const MINI_CHART_DAYS = 14;

// Fixed-footprint wrapper; renders blank while loading / when there's no data,
// so the row never shifts and an empty series degrades gracefully.
const Wrap: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <div className={styles.wrap}>{children}</div>
);

// Net momentum per day = additions − removals. These hit the same TRPC
// procedures the detail pages use, so each row-fetch warms the React Query
// cache for fast detail navigation.
const ArtistMiniChart: React.FC<{ entityId: string; date?: Date }> = ({ entityId, date }) => {
  const query = useArtistPlaylistScoreChart({ artistId: entityId, date });
  const values = useMemo(
    () => (query.data?.data ?? []).map((d) => d.gainScore - d.lossScore).slice(-MINI_CHART_DAYS),
    [query.data]
  );
  return <Wrap>{values.length > 0 ? <MiniBarChart values={values} /> : null}</Wrap>;
};

const TrackMiniChart: React.FC<{ entityId: string; date?: Date }> = ({ entityId, date }) => {
  const query = useTrackPlaylistScoreChart({ trackId: entityId, date });
  const values = useMemo(
    () => (query.data?.data ?? []).map((d) => d.gainScore - d.lossScore).slice(-MINI_CHART_DAYS),
    [query.data]
  );
  return <Wrap>{values.length > 0 ? <MiniBarChart values={values} /> : null}</Wrap>;
};

// Playlists have no gain/loss score, so chart day-over-day follower movement.
const PlaylistMiniChart: React.FC<{ entityId: string; date?: Date }> = ({ entityId, date }) => {
  const query = usePlaylistFollowersTimeSeries({ playlistId: entityId, endDate: date });
  const values = useMemo(() => {
    const series = query.data?.data ?? [];
    return series
      .map((d, index) => (index === 0 ? 0 : d.count - (series[index - 1]?.count ?? d.count)))
      .slice(-MINI_CHART_DAYS);
  }, [query.data]);
  return <Wrap>{values.length > 0 ? <MiniBarChart values={values} /> : null}</Wrap>;
};

const MiniScoreChartComponent: React.FC<MiniScoreChartProps> = ({ entityType, entityId, date }) => {
  switch (entityType) {
    case 'artist':
      return <ArtistMiniChart entityId={entityId} date={date} />;
    case 'track':
      return <TrackMiniChart entityId={entityId} date={date} />;
    case 'playlist':
      return <PlaylistMiniChart entityId={entityId} date={date} />;
    default:
      return null;
  }
};

export const MiniScoreChart = TypedMemo(MiniScoreChartComponent);
